/**
 * Safety: the hazard register, safety reports and security incidents.
 *
 * The platform's own shapes (routers/safety.py, routers/security.py), in the
 * client's words. Three registers, kept apart on purpose (migration 0019):
 *
 *   - a REPORT is an account somebody filed — it may be open (named),
 *     confidential (named, seen only by the safety office) or anonymous (no
 *     name anywhere, ever);
 *   - a HAZARD is a condition on the register, scored against the pack's
 *     matrix — and "never assessed" is a state of its own, never "acceptable";
 *   - a SECURITY INCIDENT is an occurrence in the security register, with a
 *     pack category, which is what the executive "security" figure counts.
 *
 * Nothing here imports anything heavy: MockSource reads it, and MockSource is
 * in the entry chunk.
 */
import { AERODROME_ZONE } from "../lib/format";

/* ── the matrix ──────────────────────────────────────────────────────────── */

export type Tolerability = "acceptable" | "tolerable" | "intolerable" | "unknown";
export type RiskState = "never_assessed" | "unclassified" | "stale" | "assessed";

/** A hazard's risk as a state, never a bare number (hazards.risk_of). */
export interface RiskVerdict {
  state: RiskState;
  tolerability: Tolerability | null;
  riskIndex?: string;
  /** the pack's codes as strings: likelihood "5".."1", severity "A".."E" */
  likelihood?: string;
  severity?: string;
  kind?: AssessmentKind;
  matrix?: string;
  assessedAt?: string;
  why?: string | null;
}

export interface MatrixLevel {
  code: string;
  name: string;
  rank: number;
}

export interface SafetyMatrix {
  key: string;
  version: string;
  name: string;
  likelihood: MatrixLevel[];
  severity: MatrixLevel[];
  /** "4B" → "intolerable", exactly as the pack declares each cell */
  cells: Record<string, Tolerability>;
  reviewMonths: Record<string, number>;
}

export interface HazardCategory {
  key: string;
  name: string;
  description?: string;
  domain?: string;
  defaultMatrix?: string;
}

export interface SafetyVocabulary {
  matrices: SafetyMatrix[];
  categories: HazardCategory[];
  issues: string[];
}

/** What the pack's matrix says about one pair; `unknown` where it declares no cell. */
export function bandOf(m: SafetyMatrix | undefined, likelihood: string, severity: string): Tolerability {
  return m?.cells[`${likelihood}${severity}`] ?? "unknown";
}

/* ── hazards ─────────────────────────────────────────────────────────────── */

export const HAZARD_STATUSES = ["open", "assessed", "mitigating", "monitored", "closed", "rejected"] as const;
export type HazardStatus = (typeof HAZARD_STATUSES)[number];
export const LIVE_HAZARD: readonly HazardStatus[] = ["open", "assessed", "mitigating", "monitored"];

export const ASSESSMENT_KINDS = ["initial", "residual", "review", "post_event"] as const;
export type AssessmentKind = (typeof ASSESSMENT_KINDS)[number];

export const CONTROL_TYPES = [
  "elimination",
  "substitution",
  "engineering",
  "administrative",
  "ppe",
  "monitoring",
] as const;
export type ControlType = (typeof CONTROL_TYPES)[number];

export interface ReviewState {
  state: "not_applicable" | "no_review_date" | "overdue" | "scheduled";
  due: string | null;
  daysOverdue?: number;
  daysRemaining?: number;
}

export interface Hazard {
  id: string;
  reference: string;
  category: string;
  domain?: string;
  title: string;
  description?: string;
  status: HazardStatus;
  origin: string;
  siteId?: string;
  zoneCode?: string;
  ownerId?: string;
  ownerName?: string;
  /** the department or role named on the form, when no person owns it yet */
  ownerLabel?: string;
  area?: string;
  identifiedAt: string;
  reviewDue?: string;
  closedAt?: string;
  openActions: number;
  overdueActions: number;
  riskAccepted: boolean;
  /** demonstration data the platform's own scripts planted */
  demo: boolean;
  /** the current verdict: the latest assessment of any kind */
  risk: RiskVerdict;
  /** before the controls: the latest `initial` assessment */
  initialRisk: RiskVerdict;
  /** every action not cancelled, done or still open */
  controls: string[];
  review: ReviewState;
}

export interface HazardPage {
  items: Hazard[];
  /** the register's own total, tenant-wide, so an empty list can say which kind of empty */
  total: number;
  truncated: boolean;
}

export interface HazardAction {
  seq: number;
  description: string;
  controlType: ControlType;
  dueDate?: string;
  status: "open" | "in_progress" | "done" | "cancelled";
  completedAt?: string;
  overdue: boolean;
}

export interface HazardAssessment {
  kind: AssessmentKind;
  likelihood: string;
  severity: string;
  riskIndex: string;
  tolerability: Tolerability;
  matrix: string;
  rationale?: string;
  assessedAt: string;
}

export interface HazardDetail extends Hazard {
  assessments: HazardAssessment[];
  actions: HazardAction[];
  reports: { id: string; reference: string; status: ReportStatus; reportedAt: string; confidentiality: Confidentiality }[];
  acceptances: { acceptedAt: string; rationale: string; riskIndex?: string; tolerability?: string }[];
  closureNote?: string;
}

export interface RaiseHazardInput {
  siteId: string;
  category: string;
  title: string;
  description?: string;
  origin?: string;
  /** where it is, in words; kept in the hazard's attrs */
  area?: string;
  /** who owns it, in words, until a person is named */
  ownerLabel?: string;
  reviewDue?: string;
}

export interface AssessHazardInput {
  likelihood: string;
  severity: string;
  kind: AssessmentKind;
  rationale?: string;
  reviewDue?: string;
}

export interface AssessedHazard {
  status: HazardStatus;
  risk: { riskIndex: string; tolerability: Tolerability };
  reviewDue?: string;
  escalated: boolean;
  warnings: string[];
}

export interface HazardActionInput {
  description: string;
  controlType: ControlType;
  dueDate?: string;
}

export interface CloseHazardInput {
  note: string;
  decision: "closed" | "rejected";
  /** required when the current verdict is intolerable; refused without it */
  acceptanceRationale?: string;
}

/* ── reports ─────────────────────────────────────────────────────────────── */

export const CONFIDENTIALITIES = ["open", "confidential", "anonymous"] as const;
export type Confidentiality = (typeof CONFIDENTIALITIES)[number];

export const REPORT_STATUSES = ["received", "triaged", "investigating", "closed", "rejected"] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export const REPORT_KINDS = ["hazard", "occurrence", "suggestion", "complaint"] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];

/** The board's own words for each kind, as it has always shown them. */
export const REPORT_KIND_LABEL: Record<ReportKind, string> = {
  hazard: "Hazard report",
  occurrence: "Occurrence report",
  suggestion: "Safety suggestion",
  complaint: "Complaint",
};

export type ReporterState =
  | { state: "named"; name?: string }
  | { state: "withheld"; why?: string }
  | { state: "none"; why?: string };

export interface SafetyReport {
  id: string;
  reference: string;
  confidentiality: Confidentiality;
  mandatory: boolean;
  category?: string;
  title: string;
  /** absent from a listing for a protected report — read it one at a time */
  narrative?: string;
  occurredAt?: string;
  reportedAt: string;
  status: ReportStatus;
  siteId?: string;
  hazardId?: string;
  hazardReference?: string;
  outcome?: string;
  closedAt?: string;
  kind?: string;
  immediate: boolean;
  /** "incident" when it was filed from an incident panel rather than the safety board */
  register?: string;
  /** absent for a protected report */
  where?: string;
  attrs?: Record<string, unknown>;
  demo: boolean;
  reporter: ReporterState;
}

export interface SafetyReportPage {
  items: SafetyReport[];
  /** tenant-wide */
  total: number;
  note?: string;
}

export interface FileReportInput {
  siteId: string;
  title: string;
  narrative: string;
  confidentiality: Confidentiality;
  kind?: ReportKind;
  where?: string;
  immediate?: boolean;
  occurredAt?: string;
  category?: string;
  /** a hazard already on the register this report is about */
  hazardId?: string;
  /** anything else the form recorded; merged with kind, where and immediate */
  attrs?: Record<string, unknown>;
}

export interface FiledReport {
  id: string;
  reference: string;
  status: ReportStatus;
  reportedAt: string;
  confidentiality: Confidentiality;
  reporterRecorded: boolean;
  /** what the platform will do with the filer's name — shown to them */
  note: string;
  replayed?: boolean;
}

/**
 * The title a report is filed under.
 *
 * The listing never hides a title, so for a confidential or anonymous report
 * it must say nothing a colleague could place: never the narrative, never the
 * place. Only an open report carries where it happened.
 */
export function reportTitle(kind: ReportKind | undefined, confidentiality: Confidentiality, where?: string): string {
  const label = REPORT_KIND_LABEL[kind ?? "occurrence"];
  const place = (where ?? "").trim();
  return confidentiality === "open" && place ? `${label} — ${place}` : label;
}

/* ── security incidents ──────────────────────────────────────────────────── */

export const INCIDENT_STATUSES = ["open", "investigating", "contained", "closed", "cancelled"] as const;
export type IncidentStatus = (typeof INCIDENT_STATUSES)[number];

/** incidents.TRANSITIONS — a transition to the same state is refused */
export const INCIDENT_TRANSITIONS: Record<IncidentStatus, readonly IncidentStatus[]> = {
  open: ["investigating", "contained", "closed", "cancelled"],
  investigating: ["contained", "closed", "cancelled"],
  contained: ["closed", "investigating"],
  closed: [],
  cancelled: [],
};

export interface IncidentCategory {
  key: string;
  name: string;
  description?: string;
  /** 1..5, 5 worst */
  defaultSeverity: number;
  defaultClassification: "public" | "internal" | "restricted";
  reportable: boolean;
  reportTo?: string;
  reportWithinHours?: number;
}

export interface SecurityIncident {
  id: string;
  reference: string;
  category: string;
  /** 1..5, 5 worst — the opposite way round from the board's 1 = serious */
  severity: number;
  status: IncidentStatus;
  classification: "public" | "internal" | "restricted";
  title: string;
  zoneCode?: string;
  detectedAt: string;
  occurredAt?: string;
  closedAt?: string;
  reportable: boolean;
  notifiedAt?: string;
  summary?: string;
  detail: Record<string, unknown>;
  outcome?: string;
  reportedByName?: string;
}

export interface SecurityIncidentPage {
  items: SecurityIncident[];
  /** false when restricted incidents exist that this reader may not see */
  includesRestricted: boolean;
}

export interface RaiseIncidentInput {
  siteId: string;
  category: string;
  title: string;
  severity?: number;
  summary?: string;
  occurredAt?: string;
  detail?: Record<string, unknown>;
}

export interface RaisedIncident {
  id: string;
  reference: string;
  detectedAt: string;
  notification?: { required: boolean; to?: string; withinHours?: number; note: string };
  replayed?: boolean;
}

/* ── anomaly acknowledgements ───────────────────────────────────────────── */

/** The site-note topic an anomaly acknowledgement is kept under. */
export const ANOMALY_ACK_TOPIC = "intelligence.anomaly-ack";

/* ── dates ──────────────────────────────────────────────────────────────── */

const dayFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: AERODROME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** The calendar day at the aerodrome — not the UTC day the ISO string starts with. */
export function siteDay(iso: string | null | undefined): string {
  if (!iso) return "";
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? iso.slice(0, 10) : dayFmt.format(ms);
}
